import { ChangeDetectionStrategy, Component, HostListener, computed, input, output } from '@angular/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { isEmptyButtonLabel } from '@common/helpers/forms';

import type { FormButtonSettingsData, LoginEduRegistrationButtonLegacy } from '@sites/entity-core';
import type { LoginEduForm } from '@sites/entity-signal-form';

import { SpFormButtonSettingsSignalComponent } from '@components/sp-form-button-settings-signal';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

/**
 * Styling panel for the "sign up" modal trigger. Besides the shared button
 * settings it also edits the labels of the two alternative inner buttons
 * (`enrollButton` / `enterButton`), which the visitor sees instead of "sign up"
 * once they are logged in — all three live on the same `buttons[index]` entry.
 */
@Component({
    selector: 'web-registration-trigger-settings',
    templateUrl: './registration-trigger-settings.component.html',
    styleUrls: ['./registration-trigger-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpFormButtonSettingsSignalComponent, TooltipModule],
})
export class RegistrationTriggerSettingsComponent {
    public readonly form = input.required<LoginEduForm>();
    public readonly index = input.required<number>();
    public readonly isForNewUser = input(false);

    public readonly saveEvent = output<void>();
    public readonly cancelEvent = output<void>();

    private readonly button = computed<LoginEduRegistrationButtonLegacy | undefined>(
        () => this.form().buttons().value()[this.index()] as LoginEduRegistrationButtonLegacy | undefined
    );

    public readonly settings = computed<FormButtonSettingsData | null>(() => this.button()?.modalTrigger ?? null);

    public readonly enrollButtonLabel = computed<string>(() => this.button()?.enrollButton?.label ?? '');
    public readonly enterButtonLabel = computed<string>(() => this.button()?.enterButton?.label ?? '');

    public readonly isEmptyEnrollButtonLabel = computed(() => isEmptyButtonLabel(this.enrollButtonLabel()));
    public readonly isEmptyEnterButtonLabel = computed(() => isEmptyButtonLabel(this.enterButtonLabel()));

    /**
     * The trigger renders one of three captions depending on the visitor's
     * enrolment state, so all three texts are required.
     *
     * New users don't get the "enroll"/"enter" captions (those fields are hidden),
     * so only the default label is required for them.
     */
    public readonly isSaveDisabled = computed(() => {
        if (this.isForNewUser()) {
            return isEmptyButtonLabel(this.settings()?.label);
        }

        return (
            isEmptyButtonLabel(this.settings()?.label) ||
            this.isEmptyEnrollButtonLabel() ||
            this.isEmptyEnterButtonLabel()
        );
    });

    public onSettingsChange(value: FormButtonSettingsData): void {
        const current = this.button();
        if (!current) return;

        // Spread over the current trigger so `isActive` (owned by the main
        // panel's checkbox) survives a styling edit.
        this.form().updateButton(this.index(), { modalTrigger: { ...current.modalTrigger, ...value } });
    }

    public onEnrollLabelChange(label: string): void {
        const current = this.button();
        if (!current) return;

        this.form().updateButton(this.index(), { enrollButton: { ...current.enrollButton, label } });
    }

    public onEnterLabelChange(label: string): void {
        const current = this.button();
        if (!current) return;

        this.form().updateButton(this.index(), { enterButton: { ...current.enterButton, label } });
    }

    public onSave(): void {
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        if (this.isSaveDisabled()) return;

        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }
}
